import axios from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'

const API_URL = import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_URL

export type MembershipKind = 'org' | 'tenant'

export interface Membership {
  kind: MembershipKind
  entityId: string
  name: string
  role: string
  orgId?: string
  isActive: boolean
}

interface MembershipsResponse {
  success: boolean
  data: Membership[]
}

export interface MemberRow {
  sub: string
  email: string
  name: string
  role: string
  grantedBy?: string
  createdAt?: string
}

export interface PendingInvitation {
  invitationId: string
  email: string
  role: string
  magicLink: string
}

export type InviteResult =
  | { kind: 'instant'; member: MemberRow }
  | { kind: 'pending'; invitation: PendingInvitation }

export interface InvitationPreview {
  id: string
  entityType: 'org' | 'tenant'
  entityId: string
  entityName: string
  parentOrgName?: string
  role: string
  email: string
  status: string
}

interface ActiveContextResponse {
  success: boolean
  data: {
    activeOrgId: string
    activeTenantId: string | null
  }
}

async function authHeaders(): Promise<Record<string, string>> {
  const session = await fetchAuthSession()
  const token = session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString()
  if (!token) throw new Error('Not authenticated')
  return { Authorization: `Bearer ${token}` }
}

export const profileService = {
  async getMemberships(): Promise<Membership[]> {
    const headers = await authHeaders()
    const response = await axios.get<MembershipsResponse>(`${API_URL}/me/memberships`, { headers })
    return response.data.data || []
  },

  async setActiveContext(payload: { orgId?: string; tenantId?: string }): Promise<{
    activeOrgId: string
    activeTenantId: string | null
  }> {
    const headers = await authHeaders()
    const response = await axios.post<ActiveContextResponse>(
      `${API_URL}/me/active-context`,
      payload,
      { headers },
    )
    return response.data.data
  },

  async renameOrg(orgId: string, name: string): Promise<{ id: string; name: string }> {
    const headers = await authHeaders()
    const response = await axios.patch<{ success: boolean; data: { id: string; name: string } }>(
      `${API_URL}/me/orgs/${encodeURIComponent(orgId)}`,
      { name },
      { headers },
    )
    return response.data.data
  },

  async renameTenant(tenantId: string, name: string): Promise<{ id: string; name: string }> {
    const headers = await authHeaders()
    const response = await axios.patch<{ success: boolean; data: { id: string; name: string } }>(
      `${API_URL}/me/tenants/${encodeURIComponent(tenantId)}`,
      { name },
      { headers },
    )
    return response.data.data
  },

  async createTenant(name?: string): Promise<{ id: string; name: string; orgId: string }> {
    const headers = await authHeaders()
    const response = await axios.post<{ success: boolean; data: { id: string; name: string; orgId: string } }>(
      `${API_URL}/me/tenants`,
      name ? { name } : {},
      { headers },
    )
    return response.data.data
  },

  async listOrgMembers(orgId: string): Promise<MemberRow[]> {
    const headers = await authHeaders()
    const response = await axios.get<{ success: boolean; data: MemberRow[] }>(
      `${API_URL}/me/orgs/${encodeURIComponent(orgId)}/members`,
      { headers },
    )
    return response.data.data || []
  },

  async inviteOrgMember(orgId: string, email: string, role: string): Promise<InviteResult> {
    const headers = await authHeaders()
    const response = await axios.post<{ success: boolean; pending?: boolean; data: MemberRow | PendingInvitation }>(
      `${API_URL}/me/orgs/${encodeURIComponent(orgId)}/members`,
      { email, role },
      { headers },
    )
    if (response.data.pending) {
      return { kind: 'pending', invitation: response.data.data as PendingInvitation }
    }
    return { kind: 'instant', member: response.data.data as MemberRow }
  },

  async removeOrgMember(orgId: string, sub: string): Promise<void> {
    const headers = await authHeaders()
    await axios.delete(
      `${API_URL}/me/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(sub)}`,
      { headers },
    )
  },

  async listTenantMembers(tenantId: string): Promise<MemberRow[]> {
    const headers = await authHeaders()
    const response = await axios.get<{ success: boolean; data: MemberRow[] }>(
      `${API_URL}/me/tenants/${encodeURIComponent(tenantId)}/members`,
      { headers },
    )
    return response.data.data || []
  },

  async inviteTenantMember(tenantId: string, email: string, role: string): Promise<InviteResult> {
    const headers = await authHeaders()
    const response = await axios.post<{ success: boolean; pending?: boolean; data: MemberRow | PendingInvitation }>(
      `${API_URL}/me/tenants/${encodeURIComponent(tenantId)}/members`,
      { email, role },
      { headers },
    )
    if (response.data.pending) {
      return { kind: 'pending', invitation: response.data.data as PendingInvitation }
    }
    return { kind: 'instant', member: response.data.data as MemberRow }
  },

  async removeTenantMember(tenantId: string, sub: string): Promise<void> {
    const headers = await authHeaders()
    await axios.delete(
      `${API_URL}/me/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(sub)}`,
      { headers },
    )
  },

  async getInvitation(id: string): Promise<InvitationPreview> {
    const headers = await authHeaders()
    const response = await axios.get<{ success: boolean; data: InvitationPreview }>(
      `${API_URL}/me/invitations/${encodeURIComponent(id)}`,
      { headers },
    )
    return response.data.data
  },

  async acceptInvitation(id: string): Promise<{ entityType: string; entityId: string; role: string }> {
    const headers = await authHeaders()
    const response = await axios.post<{ success: boolean; data: { entityType: string; entityId: string; role: string } }>(
      `${API_URL}/me/invitations/${encodeURIComponent(id)}/accept`,
      {},
      { headers },
    )
    return response.data.data
  },
}
